import React, { useState, useEffect, useRef } from 'react'
import { ErrorMessage, useField } from 'formik'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import { Icon } from '@material-ui/core'
import { isEmpty, not } from 'ramda'

const SelectorFormik = ({ label, name, options, placeholder, onChange }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [field, meta, helpers] = useField(name)
  const { setValue } = helpers
  const dropdownRef = useRef(null)

  const hasValue = not(isEmpty(field.value))

  // Toggle dropdown open/close
  const handleSelect = (option) => {
    if (onChange) {
      onChange(option.id)
    }
    setValue(option.id)
    setIsOpen(false)
  }

  // Handle click outside to close dropdown
  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false)
    }
  }

  // Add event listener for clicks outside the dropdown
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isOpen])

  return (
    <div className="relative mb-4" ref={dropdownRef}>
      <label className="w-fit flex text-14 text-blue-950 mb-4">{label}</label>

      {/* Dropdown toggle */}
      <div
        className={clsx(
          'p-14 h-48 rounded-8 border border-grey-input cursor-pointer flex justify-between items-center',
          hasValue ? 'text-blue-950' : 'text-grey-300',
        )}
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-controls={`${name}-listbox`}
        role="button"
        tabIndex={0}
      >
        <span>
          {hasValue
            ? options.find(
                (option) => parseInt(option.id) === parseInt(field.value),
              ).text
            : placeholder}
        </span>
        {/* Icon for dropdown */}
        <span>
          {isOpen ? <Icon>arrow_drop_up</Icon> : <Icon>arrow_drop_down</Icon>}
        </span>
      </div>

      {/* Dropdown options */}
      {isOpen && (
        <ul
          id={`${name}-listbox`}
          role="listbox"
          className="absolute z-10 bg-white border border-gray-300 w-full mt-1 rounded-md shadow-lg p-0"
        >
          {options.map((option) => (
            <li
              key={option.id}
              className="relative py-2 pl-10 pr-4 contents cursor-pointer"
              onClick={() => handleSelect(option)}
              role="option"
              aria-selected={field.value === option.id.toString()}
            >
              <span
                className={clsx(
                  'block truncate m-auto p-5 flex items-center hover:bg-slate-300',
                  field.value === option.id.toString()
                    ? 'font-medium bg-primary-lighter'
                    : 'font-normal',
                )}
              >
                {option.text}
              </span>
            </li>
          ))}
        </ul>
      )}

      {/* Display validation error */}
      <ErrorMessage
        name={name}
        component="div"
        className="text-red-500 text-sm mt-2"
      />
    </div>
  )
}

SelectorFormik.propTypes = {
  label: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      text: PropTypes.string.isRequired,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  onChange: PropTypes.func,
}

export default SelectorFormik
